/**
 * Werkseitige Fahrzeugprofile – originalgetreu abgestimmt.
 * Der Klang wird prozedural synthetisiert (kein Sample). Die Parameter steuern
 * den AudioWorklet-Synthesizer und die Antriebssimulation.
 *
 * @typedef {Object} Profile
 * @property {string} id         Eindeutige ID
 * @property {string} flag       Flaggen-Emoji
 * @property {string} brand      Marke
 * @property {string} name       Modellname
 * @property {string} engineType Motorart
 * @property {string} spec       Technische Kurzbeschreibung
 * @property {string} sub        Klangbeschreibung
 * @property {number} cylinders  Zylinderzahl → Zündfrequenz
 * @property {number} idle       Leerlaufdrehzahl (U/min)
 * @property {number} redline    Drehzahlgrenze (U/min)
 * @property {number} subOrder   0..1 Half-Order-Burble (Crossplane hoch, Flatplane ≈0)
 * @property {number} lumpiness  0..1 Zündungleichförmigkeit (Leerlauf-Bollern)
 * @property {number} brightness 0..1 Obertonneigung (NA hell, Turbo gedämpft)
 * @property {number} rasp       0..1 Growl/Sättigung
 * @property {number} whine      0..1 Kompressor-/Lader-Pfeifen
 * @property {number} whineRatio Lader-Übersetzung (Whine = f_rot · ratio)
 * @property {number} turbo      0..1 Turbo-/Ansaugrauschen
 * @property {string} color      Akzentfarbe
 * @property {string[]} tags     Schlagworte
 * @property {boolean} [custom]  true bei eigenen Profilen
 */

/** @type {Profile[]} */
export const PROTOTYPE_TEST_RUN_PROFILE_IDS = ['mustang', 'gtr', 'aventador-svj', 'lfa']

/** @type {Profile[]} */
export const FACTORY_PROFILES = [
  {
    id: 'mustang',
    flag: '🇺🇸',
    brand: 'Ford',
    name: 'Mustang GT',
    engineType: 'V8 Sauger',
    spec: '5.0L Coyote · Crossplane · 10-Gang Automatik',
    sub: 'Klassisches Crossplane-Muscle-Bollern, tiefer Half-Order-Burble und sauberer Offload.',
    soundModelId: 'mustang-v8-crossplane',
    cylinders: 8,
    idle: 800,
    redline: 7400,
    subOrder: 0.72,
    lumpiness: 0.55,
    brightness: 0.3,
    rasp: 0.5,
    whine: 0,
    whineRatio: 26,
    turbo: 0,
    color: '#42f3d2',
    tags: ['V8', 'CROSSPLANE', 'MUSCLE'],
  },
  {
    id: 'gtr',
    flag: '🇯🇵',
    brand: 'Nissan',
    name: 'GT-R R35',
    engineType: 'V6 Biturbo',
    spec: '3.8L VR38DETT · Twin-Turbo · 6-Gang DCT',
    sub: 'Kurzer, aggressiver V6-Punch mit Twin-Spool-Rauschen und kantigem Metallkörper.',
    soundModelId: 'gtr-v6-biturbo',
    cylinders: 6,
    idle: 760,
    redline: 7100,
    subOrder: 0.22,
    lumpiness: 0.28,
    brightness: 0.44,
    rasp: 0.54,
    whine: 0,
    whineRatio: 26,
    turbo: 0.5,
    color: '#c8d2df',
    tags: ['V6', 'BITURBO', 'JDM'],
  },
  {
    id: 'aventador-svj',
    flag: '🇮🇹',
    brand: 'Lamborghini',
    name: 'Aventador SVJ',
    engineType: 'V12 Sauger',
    spec: '6.5L L539 · NA V12 · ISR · 8500 U/min Power Peak',
    sub: 'Breiter, brutaler Sauger-V12 mit trockenem Metallkörper und dramatischem Top-End.',
    soundModelId: 'aventador-svj-v12-na',
    cylinders: 12,
    idle: 880,
    redline: 8700,
    subOrder: 0.08,
    lumpiness: 0.12,
    brightness: 0.72,
    rasp: 0.72,
    whine: 0,
    whineRatio: 26,
    turbo: 0,
    color: '#f05a28',
    tags: ['V12', 'SAUGER', 'SVJ'],
  },
  {
    id: 'lfa',
    flag: '🇯🇵',
    brand: 'Lexus',
    name: 'LFA',
    engineType: 'V10 Sauger',
    spec: '4.8L 1LR-GUE · Yamaha Acoustic Tuning · 9000 U/min',
    sub: 'Extrem schneller, heller V10-Wail mit sauberer Ansaugkrone und wenig Tiefbass-Matsch.',
    soundModelId: 'lfa-v10-na',
    cylinders: 10,
    idle: 900,
    redline: 9000,
    subOrder: 0.08,
    lumpiness: 0.1,
    brightness: 0.82,
    rasp: 0.58,
    whine: 0,
    whineRatio: 26,
    turbo: 0,
    color: '#f8f1e5',
    tags: ['V10', 'SAUGER', 'LFA'],
  },
  {
    id: 'hellcat',
    flag: '🇺🇸',
    brand: 'Dodge',
    name: 'Challenger Hellcat',
    engineType: 'V8 Kompressor',
    spec: '6.2L HEMI Supercharged · Crossplane',
    sub: 'Brutaler Tiefbass mit unverkennbarem Kompressor-Pfeifen, das mit der Drehzahl ansteigt.',
    soundModelId: 'hellcat-v8-supercharged',
    cylinders: 8,
    idle: 850,
    redline: 6200,
    subOrder: 0.6,
    lumpiness: 0.48,
    brightness: 0.24,
    rasp: 0.55,
    whine: 0.55,
    whineRatio: 30,
    turbo: 0.12,
    color: '#ffbd54',
    tags: ['V8', 'KOMPRESSOR', 'WHINE'],
  },
  {
    id: 'camaro',
    flag: '🇺🇸',
    brand: 'Chevrolet',
    name: 'Camaro ZL1',
    engineType: 'V8 Kompressor',
    spec: '6.2L LT4 Supercharged · 10-Gang Automatik',
    sub: 'Aggressiver Punch-Bass mit härterem Lader-Whine und direktem Schub.',
    soundModelId: 'camaro-zl1-v8-supercharged',
    cylinders: 8,
    idle: 820,
    redline: 6500,
    subOrder: 0.52,
    lumpiness: 0.44,
    brightness: 0.3,
    rasp: 0.52,
    whine: 0.48,
    whineRatio: 28,
    turbo: 0.1,
    color: '#83ff65',
    tags: ['V8', 'KOMPRESSOR', 'ZL1'],
  },
  {
    id: 'urus',
    flag: '🇮🇹',
    brand: 'Lamborghini',
    name: 'Urus Performante',
    engineType: 'V8 Biturbo',
    spec: '4.0L Hot-V Biturbo · 8-Gang Automatik',
    sub: 'Druckvoller, leicht gedämpfter Biturbo-Body mit deutlichem Ansaug-/Turbo-Rauschen.',
    soundModelId: 'urus-v8-biturbo',
    cylinders: 8,
    idle: 800,
    redline: 6800,
    subOrder: 0.4,
    lumpiness: 0.38,
    brightness: 0.26,
    rasp: 0.46,
    whine: 0,
    whineRatio: 26,
    turbo: 0.52,
    color: '#b5ff52',
    tags: ['V8', 'BITURBO', 'SUV'],
  },
  {
    id: 'supra',
    flag: '🇯🇵',
    brand: 'Toyota',
    name: 'GR Supra',
    engineType: 'I6 Turbo',
    spec: '3.0L B58 · Single-Turbo · 8-Gang Automatik',
    sub: 'Glatter Reihen-Sechser mit Single-Turbo-Spool, sauberem Mid und kurzem Lift-Off-Hiss.',
    soundModelId: 'supra-i6-turbo',
    cylinders: 6,
    idle: 740,
    redline: 7000,
    subOrder: 0.12,
    lumpiness: 0.14,
    brightness: 0.46,
    rasp: 0.44,
    whine: 0,
    whineRatio: 26,
    turbo: 0.46,
    color: '#f2d15c',
    tags: ['I6', 'TURBO', 'SPOOL'],
  },
  {
    id: 'huayra',
    flag: '🇮🇹',
    brand: 'Pagani',
    name: 'Huayra',
    engineType: 'V12 Biturbo',
    spec: '6.0L AMG M158 V12 Biturbo · Sequenziell',
    sub: 'Sahniger, exotischer V12 mit Turbo-Dämpfung und feiner metallischer Krone.',
    soundModelId: 'huayra-v12-biturbo',
    cylinders: 12,
    idle: 750,
    redline: 6700,
    subOrder: 0.12,
    lumpiness: 0.12,
    brightness: 0.42,
    rasp: 0.5,
    whine: 0,
    whineRatio: 26,
    turbo: 0.42,
    color: '#62a9ff',
    tags: ['V12', 'BITURBO', 'EXOTIC'],
  },
  {
    id: 'f812',
    flag: '🇮🇹',
    brand: 'Ferrari',
    name: '812 Superfast',
    engineType: 'V12 Sauger',
    spec: '6.5L N/A V12 · 7-Gang DCT · 8900 U/min',
    sub: 'Schreiender Sauger-V12: trockener Body, gläserne Oberton-Krone, scharfe Schaltblips.',
    soundModelId: 'f812-v12-na',
    cylinders: 12,
    idle: 900,
    redline: 8900,
    subOrder: 0.06,
    lumpiness: 0.1,
    brightness: 0.78,
    rasp: 0.62,
    whine: 0,
    whineRatio: 26,
    turbo: 0,
    color: '#ff6c5f',
    tags: ['V12', 'SAUGER', 'SCREAMER'],
  },
  {
    id: 'gt3',
    flag: '🇩🇪',
    brand: 'Porsche',
    name: '911 GT3',
    engineType: 'Boxer-6 Sauger',
    spec: '4.0L Flat-Six · PDK · 9000 U/min',
    sub: 'Metallisches Flat-Six-Reißen mit fokussiertem Mid und blitzschnellem Ansprechen.',
    soundModelId: 'gt3-flat-six-na',
    cylinders: 6,
    idle: 900,
    redline: 9000,
    subOrder: 0.16,
    lumpiness: 0.24,
    brightness: 0.72,
    rasp: 0.66,
    whine: 0,
    whineRatio: 26,
    turbo: 0,
    color: '#7ae3ff',
    tags: ['FLAT-6', 'SAUGER', 'GT3'],
  },
  {
    id: 'r8',
    flag: '🇩🇪',
    brand: 'Audi',
    name: 'R8 Performance',
    engineType: 'V10 Sauger',
    spec: '5.2L FSI V10 · S tronic · 8700 U/min',
    sub: 'Linearer V10-Wail mit heller, gleichmäßiger Drehzahlentfaltung und offener Krone.',
    soundModelId: 'r8-v10-na',
    cylinders: 10,
    idle: 850,
    redline: 8700,
    subOrder: 0.1,
    lumpiness: 0.14,
    brightness: 0.74,
    rasp: 0.6,
    whine: 0,
    whineRatio: 26,
    turbo: 0,
    color: '#d2a2ff',
    tags: ['V10', 'SAUGER', 'WAIL'],
  },
  {
    id: 'chiron',
    flag: '🇫🇷',
    brand: 'Bugatti',
    name: 'Chiron',
    engineType: 'W16 Quad-Turbo',
    spec: '8.0L W16 · Quad-Turbo · 7-Gang DCT',
    sub: 'Monströser, dichter Tiefdruck – 16 Zylinder, von vier Turbos zu ruhiger Wucht geglättet.',
    soundModelId: 'chiron-w16-quad-turbo',
    cylinders: 16,
    idle: 800,
    redline: 6700,
    subOrder: 0.24,
    lumpiness: 0.2,
    brightness: 0.3,
    rasp: 0.44,
    whine: 0,
    whineRatio: 26,
    turbo: 0.55,
    color: '#6da0ff',
    tags: ['W16', 'QUAD-TURBO', 'HYPERCAR'],
  },
  {
    id: 'amg',
    flag: '🇩🇪',
    brand: 'Mercedes-AMG',
    name: 'GT Black Series',
    engineType: 'V8 Biturbo',
    spec: '4.0L Biturbo · FLATPLANE · AMG Speedshift DCT',
    sub: 'Einzigartige Flatplane-Kurbelwelle: Ferrari-naher Wail statt AMG-Bollern, mit Turbo-Hauch.',
    soundModelId: 'amg-gt-flatplane-v8-biturbo',
    cylinders: 8,
    idle: 850,
    redline: 7200,
    subOrder: 0.05,
    lumpiness: 0.16,
    brightness: 0.58,
    rasp: 0.58,
    whine: 0,
    whineRatio: 26,
    turbo: 0.34,
    color: '#f6c46a',
    tags: ['V8', 'FLATPLANE', 'AMG'],
  },
]

/** Mixer-Presets (werden auf state.mix angewendet). */
export const PRESETS = {
  tesla: {
    master: 1.1,
    deep: 0.5,
    punch: 1.22,
    body: 1.34,
    bark: 0.82,
    edge: 0.32,
    events: 0.66,
    subGuard: 0.74,
    airfix: 0.52,
    normalizer: 1.42,
  },
  open: {
    master: 1.18,
    deep: 0.56,
    punch: 1.12,
    body: 1.42,
    bark: 0.9,
    edge: 0.3,
    events: 0.78,
    subGuard: 0.66,
    airfix: 0.48,
    normalizer: 1.48,
  },
  beast: {
    master: 1.24,
    deep: 0.58,
    punch: 1.34,
    body: 1.36,
    bark: 0.96,
    edge: 0.4,
    events: 1.06,
    subGuard: 0.58,
    airfix: 0.62,
    normalizer: 1.5,
  },
  car: {
    master: 1.04,
    deep: 0.41,
    punch: 1.14,
    body: 1.42,
    bark: 0.78,
    edge: 0.28,
    events: 0.66,
    subGuard: 0.86,
    airfix: 0.44,
    normalizer: 1.32,
  },
}
